<template>
  <div class="layout-center-wrap">
    <div class="page-tit__wrap">
      <h1 class="page-tit__sub">취약점 정보</h1>
      <h2 class="page-tit">CVE 상세보기</h2>
    </div>

    <article v-if="item">
      <!-- ── 히어로 ── -->
      <div class="entry-detail-hero">
        <div class="entry-detail-hero__meta">
          <span>취약점 상세정보</span>
          <span>등록일 {{ formatDate(item.summary.datePublished) }}</span>
        </div>
        <h2 class="entry-detail-hero__title">
          <span class="text-blue">{{ item.summary.cveId }}</span>
          · {{ item.summary.title }}
        </h2>
        <div class="entry-detail-hero__chips">
          <span
            v-if="
              item.summary.cvssScore != null && item.summary.cvssScore !== ''
            "
            :class="[
              'level-badge',
              `level-badge--${severityVariant(item.summary.cvssSeverity)}`,
            ]"
          >
            {{ formatScore(item.summary.cvssScore) }} ({{
              severityName(item.summary.cvssSeverity)
            }})
          </span>
          <span v-else class="level-badge level-badge--unknown">평가 전</span>
          <span v-if="item.summary.kev" class="tag-badge">CISA KEV</span>
          <span class="neutral-chip">
            CNA {{ item.summary.cnaName || item.summary.cnaShortName || "-" }}
          </span>
        </div>
      </div>

      <!-- ── 개요 ── -->
      <section class="detail-section">
        <h3 class="detail-section__title">개요</h3>
        <div class="detail-panel detail-panel--flush">
          <table class="info-table">
            <tbody>
              <tr>
                <th>CVE</th>
                <td>{{ item.summary.cveId }}</td>
                <th>CNA</th>
                <td>{{ item.summary.cnaName || "-" }}</td>
              </tr>
              <tr>
                <th>등록일</th>
                <td>{{ formatDate(item.summary.datePublished) }}</td>
                <th>수정일</th>
                <td>{{ formatDate(item.summary.dateUpdated) }}</td>
              </tr>
              <tr>
                <th>CVSS</th>
                <td>{{ item.summary.cvssScore ?? "-" }}</td>
                <th>위험도</th>
                <td>{{ severityName(item.summary.cvssSeverity) }}</td>
              </tr>
              <tr>
                <th>CWE</th>
                <td>{{ item.summary.cwe || "-" }}</td>
                <th>CISA KEV</th>
                <td>{{ item.summary.kev ? "등재" : "미등재" }}</td>
              </tr>
              <tr>
                <th>영향 제품</th>
                <td colspan="3">{{ item.summary.vendorProduct || "-" }}</td>
              </tr>
            </tbody>
          </table>
        </div>
      </section>

      <!-- ── 취약점 설명 ── -->
      <section class="detail-section">
        <h3 class="detail-section__title">취약점 설명</h3>
        <div class="detail-panel">
          <p class="detail-description">
            {{ item.summary.description || "-" }}
          </p>
        </div>
      </section>

      <!-- ── CVSS 평가 ── -->
      <section class="detail-section">
        <h3 class="detail-section__title">CVSS 평가</h3>
        <div class="detail-panel detail-panel--flush">
          <template v-if="item.metrics && item.metrics.length">
            <table class="data-table">
              <colgroup>
                <col width="17%" />
                <col width="17%" />
                <col width="17%" />
                <col width="50%" />
              </colgroup>
              <thead>
                <tr>
                  <th>Version</th>
                  <th>Base Score</th>
                  <th>Severity</th>
                  <th>Vector</th>
                </tr>
              </thead>
              <tbody>
                <tr v-for="(m, idx) in item.metrics" :key="idx">
                  <td data-label="Version">CVSS {{ m.version || "-" }}</td>
                  <td data-label="Base Score">{{ m.baseScore ?? "-" }}</td>
                  <td data-label="Severity">
                    {{ severityName(m.baseSeverity) }}
                  </td>
                  <td data-label="Vector">{{ m.vectorString || "-" }}</td>
                </tr>
              </tbody>
            </table>
          </template>
          <p v-else class="no-data">CVSS 데이터가 없습니다.</p>
        </div>
      </section>

      <!-- ── 취약한 제품 및 버전 ── -->
      <section class="detail-section">
        <h3 class="detail-section__title">취약한 제품 및 버전</h3>
        <template v-if="item.affected && item.affected.length">
          <article
            v-for="(af, idx) in item.affected"
            :key="idx"
            class="item-card"
          >
            <div class="item-card__head">
              <span class="item-card__name">{{ af.vendor || "-" }}</span>
              <span class="item-card__sub-name">{{ af.product || "-" }}</span>
              <span v-if="af.defaultStatus" class="item-card__status">
                기본 상태: {{ af.defaultStatusLabel || af.defaultStatus }}
              </span>
            </div>
            <div class="item-card__grid">
              <div class="item-col item-col--active">
                <span class="item-col__title">취약한 버전</span>
                <div
                  v-if="versionsByStatus(af, 'affected').length"
                  class="item-col__chips"
                >
                  <span
                    v-for="(v, vi) in versionsByStatus(af, 'affected')"
                    :key="vi"
                    class="card-chip"
                  >
                    {{ v.display }}
                    <span v-if="v.versionType" class="card-chip__meta">{{
                      v.versionType
                    }}</span>
                  </span>
                </div>
                <span v-else class="item-col__empty"
                  >취약한 버전 정보가 없습니다.</span
                >
              </div>
              <div class="item-col item-col--resolved">
                <span class="item-col__title">해결된 버전</span>
                <div
                  v-if="versionsByStatus(af, 'unaffected').length"
                  class="item-col__chips"
                >
                  <span
                    v-for="(v, vi) in versionsByStatus(af, 'unaffected')"
                    :key="vi"
                    class="card-chip"
                  >
                    {{ v.display }}
                    <span v-if="v.versionType" class="card-chip__meta">{{
                      v.versionType
                    }}</span>
                  </span>
                </div>
                <span v-else class="item-col__empty"
                  >해결된 버전 정보가 없습니다.</span
                >
              </div>
            </div>
          </article>
        </template>
        <div v-else class="detail-panel">
          <p class="no-data">Affected Products 데이터가 없습니다.</p>
        </div>
      </section>

      <!-- ── CISA KEV (조건부) ── -->
      <section
        v-if="item.summary.kev && item.summary.kevDetail"
        class="detail-section"
      >
        <h3 class="detail-section__title">CISA KEV</h3>
        <div class="detail-panel detail-panel--flush">
          <table class="info-table">
            <tbody>
              <tr>
                <th>Vendor Project</th>
                <td colspan="3">
                  {{ item.summary.kevDetail.vendorProject || "-" }}
                </td>
              </tr>
              <tr>
                <th>Product</th>
                <td colspan="3">{{ item.summary.kevDetail.product || "-" }}</td>
              </tr>
              <tr>
                <th>Vulnerability Name</th>
                <td colspan="3">
                  {{ item.summary.kevDetail.vulnerabilityName || "-" }}
                </td>
              </tr>
              <tr>
                <th>Date Added</th>
                <td>{{ item.summary.kevDetail.dateAdded || "-" }}</td>
                <th>Due Date</th>
                <td>{{ item.summary.kevDetail.dueDate || "-" }}</td>
              </tr>
              <tr>
                <th>Required Action</th>
                <td colspan="3">
                  {{ item.summary.kevDetail.requiredAction || "-" }}
                </td>
              </tr>
            </tbody>
          </table>
        </div>
      </section>

      <!-- ── 해결 방법 ── -->
      <section
        v-if="item.solutions && item.solutions.length"
        class="detail-section"
      >
        <h3 class="detail-section__title">해결 방법</h3>
        <div class="detail-panel">
          <p v-for="(block, idx) in item.solutions" :key="idx">
            {{ block.value || block.description || "" }}
          </p>
        </div>
      </section>

      <!-- ── 임시 조치 ── -->
      <section
        v-if="item.workarounds && item.workarounds.length"
        class="detail-section"
      >
        <h3 class="detail-section__title">임시 조치</h3>
        <div class="detail-panel">
          <p v-for="(block, idx) in item.workarounds" :key="idx">
            {{ block.value || block.description || "" }}
          </p>
        </div>
      </section>

      <!-- ── 참고자료 ── -->
      <section class="detail-section">
        <h3 class="detail-section__title">참고자료</h3>
        <div class="detail-panel">
          <template v-if="item.references && item.references.length">
            <ul class="ref-list">
              <li v-for="(ref, idx) in item.references" :key="idx">
                <a
                  :href="ref.url"
                  target="_blank"
                  rel="noreferrer"
                  class="ref-list__link"
                >
                  {{ ref.name || ref.url }}
                </a>
              </li>
            </ul>
          </template>
          <p v-else class="no-data">References 데이터가 없습니다.</p>
        </div>
      </section>
    </article>

    <p v-else class="no-data">
      {{
        isLoading
          ? "데이터를 불러오는 중입니다."
          : "CVE 정보를 찾을 수 없습니다."
      }}
    </p>

    <div class="btn__wrap type-bottom-board">
      <button type="button" class="btn btn--m btn--blue" @click="goBack">
        목록
      </button>
    </div>
  </div>
</template>

<script>
import dt from "@/component/utils/DateTime.js";
import { computed, inject, onMounted, ref } from "vue";
import { useRoute, useRouter } from "vue-router";
import { useStore } from "vuex";
import { useCveFormat } from "./UseCveFormat";

export default {
  setup() {
    const axios = inject("axios");
    const route = useRoute();
    const router = useRouter();
    const store = useStore();

    const showLoading = () => store.dispatch("loading/show");
    const hideLoading = () => store.dispatch("loading/hide");
    const isLoading = computed(() => store.state.loading.isLoading);

    const { severityVariant, severityName, formatScore, formatDate } =
      useCveFormat();

    // ── 상태 ────────────────────────────────────────────────────────────────
    const item = ref(null);
    const id = route.query.id || null;

    // ── API 조회 ─────────────────────────────────────────────────────────────
    const fetchDetail = (cveId) => {
      showLoading();
      axios
        .post("/core/pu/view/cve/detail/get", { cveId })
        .then((res) => {
          if (res.data.resType === "RES_OK" && res.data.resList.length > 0) {
            item.value = res.data.resList[0]; // CVEDetailDto
          } else {
            console.warn("[CveBoardDetail] CVE 없음:", cveId);
          }
        })
        .catch((err) => console.error("[CveBoardDetail] 상세 조회 실패:", err))
        .finally(() => hideLoading());
    };

    // ── 버전 필터 ─────────────────────────────────────────────────────────────
    const versionsByStatus = (af, status) =>
      (af.versions || []).filter(
        (v) => String(v.status).toLowerCase() === status
      );

    // ── 목록 이동 ─────────────────────────────────────────────────────────────
    const goBack = () => router.push({ name: "CveBoard" });

    // ── 날짜 ─────────────────────────────────────────────────────────────────
    const convertDateFormat = (date) => dt.dateFormat(date);

    // life cycle 정의
    onMounted(() => {
      if (!id) {
        router.push({ name: "CveBoard" });
        return;
      }
      fetchDetail(id);
    });

    // html에서 사용될 데이터 정의
    return {
      item,
      isLoading,
      versionsByStatus,
      goBack,
      convertDateFormat,
      severityVariant,
      severityName,
      formatScore,
      formatDate,
    };
  },
};
</script>
